/**
 * wsData — live chain polling + ready-made strategy builders for the Options Workspace.
 */
import { useEffect, useRef, useState } from 'react';
import api from '../../../services/api';
import { WsLeg, OptType, Side, bsPrice } from './wsMath';
import { OPTION_TEMPLATES, OptionTemplate, TplOutlook, tplSideQuotes, tplFarMatched } from '../../../utils/optionTemplates';

export interface WsQuote {
  symbol: string;
  mark: number | null; bid: number | null; ask: number | null;
  iv: number | null; bidIv: number | null; askIv: number | null;
  delta: number | null; gamma: number | null; theta: number | null; vega: number | null;
  oi: number | null; volume: number | null; bidSize: number | null; askSize: number | null;
}
export interface WsRow { strike: number; ce: WsQuote | null; pe: WsQuote | null }
export interface WsChain {
  base: string;
  spot: number;
  contractValue: number;
  serverTime: number;
  expiries: Array<{ iso: string; settleMs: number; strikes: number[] }>;
  chain: { iso: string; settleMs: number; rows: WsRow[] } | null;
  quotes: Record<string, WsQuote>;
}

export const POLL_MS = 3000;

/**
 * Poll the live chain every 3s (paused while the tab is hidden). `expiry` picks which
 * expiry ships full rows; `legSymbols` keeps legs on OTHER expiries priced.
 * A failed poll keeps the last good chain on screen and surfaces the error.
 */
export function useWsChain(base: string, expiry: string | null, legSymbols: string[]) {
  const [data, setData] = useState<WsChain | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastAt, setLastAt] = useState<number | null>(null);
  const symsKey = Array.from(new Set(legSymbols)).sort().join(',');
  const reqRef = useRef(0);

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      if (!alive) return;
      if (typeof document !== 'undefined' && document.hidden) { timer = setTimeout(tick, POLL_MS); return; }
      const id = ++reqRef.current;
      try {
        const r = await api.get<any>('/options/workspace/chain', {
          params: { base, expiry: expiry || undefined, symbols: symsKey || undefined },
          headers: { _suppressToast: 'true' } as any,
        });
        if (!alive || id !== reqRef.current) return;
        const d = r.data?.data as WsChain | undefined;
        if (d) { setData(d); setError(null); setLastAt(Date.now()); }
      } catch (e: any) {
        if (!alive || id !== reqRef.current) return;
        setError(e?.response?.data?.error?.message || e?.message || 'Chain unavailable');
      } finally {
        if (alive) timer = setTimeout(tick, POLL_MS);
      }
    };
    tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [base, expiry, symsKey]);

  // A base switch must never show the previous asset's chain.
  useEffect(() => { setData(null); }, [base]);

  return { data, error, lastAt };
}

/** Delta product symbol from leg parts: {C|P}-BASE-STRIKE-DDMMYY. */
export function deltaSymbol(optType: OptType, base: string, strike: number, iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${optType === 'CE' ? 'C' : 'P'}-${base}-${strike}-${d}${m}${y.slice(2)}`;
}

export function parseDeltaSymbol(sym: string): { optionType: OptType; base: string; strike: number; expiry: string } | null {
  const m = /^([CP])-([A-Z0-9]+)-(\d+(?:\.\d+)?)-(\d{2})(\d{2})(\d{2})$/.exec(String(sym || '').trim().toUpperCase());
  if (!m) return null;
  return { optionType: m[1] === 'C' ? 'CE' : 'PE', base: m[2], strike: Number(m[3]), expiry: `20${m[6]}-${m[5]}-${m[4]}` };
}

export const quoteOf = (row: WsRow | undefined, t: OptType) => (row ? (t === 'CE' ? row.ce : row.pe) : null);

/** The live mark used for a MKT leg (falls back to mid, then the side's touch). */
export function markOf(q: WsQuote | null | undefined): number | null {
  if (!q) return null;
  if (q.mark != null && q.mark > 0) return q.mark;
  if (q.bid && q.ask) return (q.bid + q.ask) / 2;
  return q.ask || q.bid || null;
}

/** Executable price for a market order on this side: SELL hits the bid, BUY lifts the ask
 *  (falls back to the mark when that side has no quote). `mark` model = the mark. */
export function execPrice(q: WsQuote | null | undefined, action: Side, model: 'touch' | 'mark' = 'touch'): number | null {
  const mk = markOf(q);
  if (model === 'mark' || !q) return mk;
  const px = action === 'SELL' ? q.bid : q.ask;
  return px != null && px > 0 ? px : mk;
}

/** Book-walk fill preview from the backend (same function the paper engine fills with). */
export interface BookFillRow {
  symbol: string; side: 'buy' | 'sell'; qty: number;
  fill: { price: number; levels: number; depthShort: boolean; best: number } | null;
  /** LIVE slicing: contracts available right now within the price cap (capPct of the premium
   *  beyond the best price), and that cap. Null when the book could not be read. */
  withinCap?: number | null; capPrice?: number | null; capPct?: number;
}
export async function fetchBookFills(legs: Array<{ symbol: string; side: 'buy' | 'sell'; qty: number }>): Promise<BookFillRow[]> {
  if (!legs.length) return [];
  const r = await api.get<any>('/options/workspace/book-fill', {
    params: { legs: legs.slice(0, 8).map(l => `${l.symbol}:${l.side}:${Math.max(1, Math.round(l.qty))}`).join(',') },
    headers: { _suppressToast: 'true' } as any,
  });
  return r.data?.data || [];
}

/** ATM strike (nearest to spot) and its averaged CE/PE IV. */
export function atmOf(rows: WsRow[], spot: number): { strike: number | null; iv: number | null } {
  if (!rows.length || !(spot > 0)) return { strike: null, iv: null };
  const r = rows.reduce((p, c) => (Math.abs(c.strike - spot) < Math.abs(p.strike - spot) ? c : p), rows[0]);
  const ivs = [r.ce?.iv, r.pe?.iv].filter((x): x is number => x != null && x > 0);
  return { strike: r.strike, iv: ivs.length ? ivs.reduce((a, b) => a + b, 0) / ivs.length : null };
}

let legSeq = 0;
export const newLegId = () => `L${Date.now().toString(36)}${(legSeq++).toString(36)}`;

/** Build a leg from a chain row (MKT at the live mark). */
export function legFromRow(base: string, row: WsRow, t: OptType, action: Side, expiry: { iso: string; settleMs: number }, lots = 1, fallbackIv = 0.5): WsLeg | null {
  const q = quoteOf(row, t);
  const px = execPrice(q, action);
  if (px == null) return null;
  return {
    id: newLegId(), action, optionType: t, strike: row.strike, expiry: expiry.iso, settleMs: expiry.settleMs,
    lots, price: px, priceMode: 'MKT', enabled: true, iv: q?.iv || fallbackIv,
    symbol: q?.symbol || deltaSymbol(t, base, row.strike, expiry.iso),
  };
}

// ── Ready-made strategies ─────────────────────────────────────────────────────

// The ready-mades live in utils/optionTemplates.ts — byte-identical on the backend, so
// a price level's "open this strategy when it fires" builds the same strikes the
// workspace card shows (scripts/check-engine-parity.mjs).
export type Outlook = TplOutlook;
export type ReadyMade = OptionTemplate;
export const READY_MADE: ReadyMade[] = OPTION_TEMPLATES;

/** Price a short would be sold at: the bid when there is one, else the mark. */
export const sellPrice = (q: WsQuote | null | undefined): number | null => (q?.bid && q.bid > 0 ? q.bid : markOf(q));

/** Side quotes (strike → mark) for one option type across chain rows. */
export const sideQuotes = (rows: WsRow[], t: OptType) => tplSideQuotes(rows, t);

/** The monthly-strangle entry (see optionTemplates.tplFarMatched). */
export const farMatched = (rows: WsRow[], spot: number) => tplFarMatched(rows, spot);

/** Materialise a ready-made into concrete legs on the current expiry (null if the chain can't host it). */
export function buildReadyMade(rm: ReadyMade, chain: WsChain, fallbackIv: number): WsLeg[] | null {
  const ch = chain.chain;
  if (!ch || !ch.rows.length) return null;
  const { strike: atm } = atmOf(ch.rows, chain.spot);
  if (atm == null) return null;
  const T = Math.max(0, ch.settleMs - Date.now()) / (365 * 86400e3);
  const spec = rm.build({ rows: ch.rows, spot: chain.spot, atm, T });
  if (!spec) return null;
  const out: WsLeg[] = [];
  for (const s of spec) {
    const row = ch.rows.find(r => r.strike === s.strike);
    if (!row) return null;
    const leg = legFromRow(chain.base, row, s.t, s.action, ch, s.lots || 1, fallbackIv);
    if (!leg) return null;
    out.push(leg);
  }
  return out;
}

/** Theoretical (model) value — used where a leg has no live quote (e.g. stepped onto an empty strike). */
export const modelPrice = (t: OptType, spot: number, k: number, settleMs: number, iv: number) =>
  bsPrice(t, spot, k, Math.max(0, settleMs - Date.now()) / (365 * 86400e3), iv);

// ── daily history (move odds, entry timing) ─────────────────────────────────

export interface DailyBar { t: number; open: number; high: number; low: number; close: number }
const dailyCache = new Map<string, { at: number; bars: DailyBar[] }>();

/** ~3 years of daily candles for the base's perp (cached 30 min per base). */
export function useDailyBars(base: string): DailyBar[] | null {
  const [bars, setBars] = useState<DailyBar[] | null>(() => dailyCache.get(base)?.bars || null);
  useEffect(() => {
    let alive = true;
    const hit = dailyCache.get(base);
    if (hit && Date.now() - hit.at < 30 * 60e3) { setBars(hit.bars); return; }
    setBars(hit?.bars || null);
    api.get<any>('/charts/candles', {
      params: { symbol: `${base}USD`, exchange: 'DELTA', interval: 1440, from: Math.floor(Date.now() / 1000) - 3 * 365 * 86400 },
      headers: { _suppressToast: 'true' } as any,
    }).then(r => {
      const raw: any[] = r.data?.data?.candles || [];
      const out = raw.map(c => ({ t: Number(c.time) > 1e12 ? Number(c.time) : Number(c.time) * 1000, open: +c.open, high: +c.high, low: +c.low, close: +c.close }))
        .filter(b => b.close > 0 && b.high > 0 && b.low > 0).sort((a, b) => a.t - b.t);
      dailyCache.set(base, { at: Date.now(), bars: out });
      if (alive) setBars(out);
    }).catch(() => { if (alive) setBars(hit?.bars || []); });
    return () => { alive = false; };
  }, [base]);
  return bars;
}

/**
 * Weekly-entry context: the last three COMPLETED daily bars. "moved" when at least two of
 * the three closed the same way and the 3-day change is ≥ 5% — the "after a 2–3 day move,
 * then sell" condition. Thresholds are the workspace's, stated in the tooltip.
 */
export function entryContext(bars: DailyBar[] | null) {
  if (!bars || bars.length < 5) return null;
  const done = bars.filter(b => b.t + 86400e3 <= Date.now());
  if (done.length < 4) return null;
  const last = done.slice(-3), ref = done[done.length - 4];
  const chg = last[2].close / ref.close - 1;
  const ups = last.filter((b, i) => b.close > (i ? last[i - 1].close : ref.close)).length;
  const sameWay = Math.max(ups, 3 - ups) >= 2;
  return { chg, ups, downs: 3 - ups, moved: sameWay && Math.abs(chg) >= 0.05 };
}
